import { useQuery } from "@tanstack/react-query";
import { axios } from "./axios";
import { useAuth } from "../context/AuthContext";
import { env } from "../utils/env";
import { PagedResponse } from "../types/Common";
import { useNavigate } from "react-router-dom";
import { ROUTE } from "../routes";
import { Profile } from "../types/Profile";

type ProfileListParams = {
  search?: string;
  type?: string;
  registrationNumber?: string;
  dateOfBirth?: string;

  page?: number;
  pageSize?: number;
  sortBy?: "asc" | "dsc";
  orderBy?: string;
  all?: boolean;
};

const fetchProfiles = async (
  authHeader: string,
  params: ProfileListParams = {},
) => {
  const {
    search,
    type,
    registrationNumber,
    dateOfBirth,
    page,
    pageSize,
    sortBy,
    orderBy,
    all,
  } = params;

  const url = new URL(`${env.verifyServiceUrl}individual/profile/`);

  if (search) url.searchParams.set("search", search);

  if (all) url.searchParams.set("all", "true");

  if (type) url.searchParams.set("_type", type);

  if (registrationNumber) {
    url.searchParams.set("registration_number", registrationNumber);
  }

  if (dateOfBirth) {
    const formattedDOB = new Date(dateOfBirth).toISOString().split("T")[0];
    url.searchParams.set("date_of_birth", formattedDOB);
  }

  if (page) url.searchParams.set("page", page.toString());
  if (Number.isInteger(pageSize))
    url.searchParams.set("page_size", pageSize!.toString());

  if (sortBy && orderBy) {
    const prefix = sortBy === "dsc" ? "-" : "";
    url.searchParams.set("ordering", `${prefix}${orderBy}`);
  }

  return axios.get<PagedResponse<Profile[]>>(url.href, {
    headers: { Authorization: authHeader },
  });
};

export const useGetProfileList = (
  params: ProfileListParams = {},
  options?: { enabled?: boolean },
) => {
  const { authHeader } = useAuth();
  const navigate = useNavigate();

  return useQuery(
    [
      "profileList",
      params.search,
      params.type,
      params.registrationNumber,
      params.dateOfBirth,
      params.page,
      params.pageSize,
      params.sortBy,
      params.orderBy,
      params.all,
    ],
    () => fetchProfiles(authHeader, params),
    {
      enabled: options?.enabled ?? true,
      keepPreviousData: true,
      retry: true,
      onError: (err: any) => {
        if (err.request?.status === 500 || err.request?.status === 503) {
          navigate(ROUTE.SERVER_ERROR);
        }
      },
    },
  );
};
